import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../lib/api';
import { Brain, RefreshCw, Loader2 } from 'lucide-react';

const CHIP = (v, palette) => {
  const p = palette[v] || 'text-[var(--txt-secondary)] border-[var(--border-default)]';
  return `px-1.5 py-0.5 text-[9px] tracking-widest uppercase border ${p}`;
};

const P_TREND    = { bullish: 'text-buy border-buy',   bearish: 'text-sell border-sell',  neutral: 'text-hold border-hold' };
const P_STRONG   = { strong: 'text-buy border-buy',    moderate: 'text-hold border-hold', weak: 'text-sell border-sell', fading: 'text-sell border-sell' };
const P_RISK     = { very_low: 'text-buy border-buy',  low: 'text-buy border-buy',        medium: 'text-hold border-hold', high: 'text-sell border-sell', very_high: 'text-sell border-sell' };
const P_REGIME   = { trending_up: 'text-buy border-buy', trending_down: 'text-sell border-sell', range: 'text-hold border-hold', high_vol: 'text-sell border-sell', low_vol: 'text-cyan-sys border-cyan-sys', transition: 'text-[var(--txt-secondary)] border-[var(--border-default)]', unknown: 'text-[var(--txt-muted)] border-[var(--border-default)]' };

/**
 * StructuredAIPanel — Phase 53.A. Replaces the old free-text AI panel
 * with a two-part view: an at-a-glance grid (trend / momentum / regime
 * / risk / confidence) + the AI's reasoning bullets + the zone list.
 * Also exposes the parsed zones + regime segments so the parent can
 * paint them onto the chart.
 */
export default function StructuredAIPanel({ symbol, timeframe, onAnalysis }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const lastRun = useRef({ s: null, tf: null });

  const run = async () => {
    if (busy || !symbol) return;
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post('/crypto/intel/analyze', { symbol, tf: timeframe });
      setData(data);
      onAnalysis?.(data);
      lastRun.current = { s: symbol, tf: timeframe };
    } catch (e) {
      // Don't clear existing analysis on transient failure — the user
      // (rightly) got annoyed when a slow LLM wiped their zones.
      setErr(e?.response?.data?.detail || 'analysis failed');
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (lastRun.current.s === symbol && lastRun.current.tf === timeframe) return;
    const t = setTimeout(run, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, timeframe]);

  const conf = data?.confidence ?? 0;
  const confColor = conf >= 75 ? 'text-buy' : conf >= 50 ? 'text-hold' : conf >= 25 ? 'text-sell' : 'text-[var(--txt-muted)]';

  return (
    <div className="border border-[var(--border-default)] bg-[var(--bg-panel)] flex flex-col" data-testid="crypto-ai-structured">
      <div className="flex items-center justify-between border-b border-[var(--border-default)] px-2 py-1.5">
        <div className="text-[10px] tracking-[0.35em] uppercase text-cyan-sys flex items-center gap-1.5">
          <Brain size={11} /> // ai read (auto)
        </div>
        <button onClick={run} disabled={busy}
                className="text-[9px] tracking-widest uppercase text-cyan-sys hover:underline flex items-center gap-1 disabled:opacity-40"
                data-testid="crypto-ai-run">
          {busy ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
          {busy ? 'analyzing…' : 'refresh'}
        </button>
      </div>

      {err && <div className="p-2 text-[10px] text-sell">// {err}</div>}
      {!data && !err && !busy && <div className="p-3 text-[10px] text-[var(--txt-muted)]">// awaiting analysis</div>}

      {data && (
        <div className="p-2 flex flex-col gap-2 text-[10px] font-mono">
          {/* Confidence gauge */}
          <div>
            <div className="flex items-baseline justify-between">
              <span className="text-[9px] tracking-[0.35em] uppercase text-[var(--txt-muted)]">confidence</span>
              <span className={`text-[24px] font-mono tabular-nums leading-none ${confColor}`} data-testid="crypto-ai-confidence">
                {conf}
              </span>
            </div>
            <div className="w-full h-1.5 bg-black border border-[var(--border-default)] mt-1 relative overflow-hidden">
              <div className={`h-full ${conf >= 75 ? 'bg-buy' : conf >= 50 ? 'bg-hold' : 'bg-sell'}`}
                   style={{ width: `${conf}%`, transition: 'width 400ms ease-out' }} />
            </div>
          </div>

          {/* Chips grid */}
          <div className="grid grid-cols-2 gap-1">
            <span className={CHIP(data.trend, P_TREND)}       data-testid="crypto-ai-trend">trend · {data.trend}</span>
            <span className={CHIP(data.momentum, P_STRONG)}    data-testid="crypto-ai-momentum">momentum · {data.momentum}</span>
            <span className={CHIP(data.risk, P_RISK)}          data-testid="crypto-ai-risk">risk · {data.risk}</span>
            <span className={CHIP(data.regime, P_REGIME)}      data-testid="crypto-ai-regime">regime · {data.regime}</span>
          </div>

          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 border-t border-[var(--border-default)] pt-1.5">
            <div><span className="text-[var(--txt-muted)]">Support</span>    <span className="text-buy tabular-nums">${Number(data.support).toFixed(4)}</span></div>
            <div><span className="text-[var(--txt-muted)]">Resistance</span> <span className="text-sell tabular-nums">${Number(data.resistance).toFixed(4)}</span></div>
            <div><span className="text-[var(--txt-muted)]">EMA</span>        <span className="tabular-nums">{data.ema_alignment}</span></div>
            <div><span className="text-[var(--txt-muted)]">Vol</span>        <span className="tabular-nums">{data.volume}</span></div>
            <div><span className="text-[var(--txt-muted)]">Liquidity</span>  <span className="tabular-nums">{data.liquidity}</span></div>
            <div><span className="text-[var(--txt-muted)]">Volatility</span> <span className="tabular-nums">{data.volatility}</span></div>
          </div>

          <div className="text-[10px] text-[var(--txt-primary)] border-t border-[var(--border-default)] pt-1.5" data-testid="crypto-ai-summary">
            {data.summary}
          </div>

          {data.reasoning && (
            <details className="text-[10px]">
              <summary className="cursor-pointer text-[var(--txt-secondary)] hover:text-cyan-sys">// why (reasoning)</summary>
              <pre className="whitespace-pre-wrap text-[10px] text-[var(--txt-secondary)] mt-1 leading-relaxed">{data.reasoning}</pre>
            </details>
          )}

          {Array.isArray(data.zones) && data.zones.length > 0 && (
            <div className="border-t border-[var(--border-default)] pt-1.5">
              <div className="text-[9px] tracking-[0.35em] uppercase text-[var(--txt-muted)] mb-1">// zones</div>
              {data.zones.map((z, i) => (
                <div key={i} data-testid={`crypto-ai-zone-${z.kind}`} className="flex items-center justify-between text-[10px] py-0.5">
                  <span className="text-cyan-sys tracking-widest uppercase">{z.kind.replace(/_/g, ' ')}</span>
                  <span className="text-[var(--txt-secondary)] tabular-nums">${Number(z.from_price).toFixed(4)} — ${Number(z.to_price).toFixed(4)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
